// src/lib/realtimeStore.ts
import { supabase } from '../supabase';
import { queryClient } from './queryClient';
import type { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Centralized Supabase Realtime manager — postgres_changes ONLY.
 *
 * One channel per tenant. Invalidates TanStack Query cache on change.
 * Components never create postgres_changes channels directly.
 *
 * Broadcast/presence/collaborative-lock channels are NOT managed here.
 * They remain in their owning hooks/components:
 *   - useRealtimeChat.js (broadcast + presence)
 *   - KassenbuchView.jsx (collaborative locks: kb-tx, kb-lock, kb-prevlock)
 *   - OverbookingLocksModal.jsx (collaborative lock)
 *   - WaitingListModal.jsx (collaborative lock)
 *
 * Usage in AuthedApp root:
 *   const { start, stop } = createRealtimeStore(tenantId, userId);
 *   useEffect(() => { start(); return stop; }, [tenantId]);
 */

/**
 * Map of table → query key(s) to invalidate.
 * When a postgres_changes event fires for a table, all queries matching
 * these keys are invalidated, triggering a background refetch.
 */
const TABLE_QUERY_MAP: Record<string, (_payload?: unknown) => void> = {
  // HR / Time tracking
  // hr_state invalidates the WHOLE 'hr' branch (owner ticket 2026-10-13):
  // previously only ['hr','state'] matched, so the Team-on-Duty widget's
  // ['hr','activeStates',...] key never refreshed on clock-in/out events and
  // the widget went stale ("funktioniert nicht zuverlässig"). ['hr'] prefix
  // matching covers both the per-user clock state and the widget query.
  hr_state: () => queryClient.invalidateQueries({ queryKey: ['hr'] }),
  time_logs: () => queryClient.invalidateQueries({ queryKey: ['hr', 'timeLogs'] }),
  hr_requests: () => {
    queryClient.invalidateQueries({ queryKey: ['hr', 'requests'] });
    queryClient.invalidateQueries({ queryKey: ['hr', 'teamRequests'] });
  },
  shifts: () => queryClient.invalidateQueries({ queryKey: ['hr', 'shifts'] }),
  shift_comments: () => queryClient.invalidateQueries({ queryKey: ['hr', 'shiftComments'] }),

  // Shift Handover (Schichtübergabe)
  shift_handovers: () => queryClient.invalidateQueries({ queryKey: ['handover'] }),
  shift_handover_items: () => queryClient.invalidateQueries({ queryKey: ['handover'] }),

  // Users
  users: () => queryClient.invalidateQueries({ queryKey: ['users'] }),

  // Reservations
  // Reservations — managed by Apaleo, not Supabase. No realtime invalidation needed.

  // Guests
  guests: () => queryClient.invalidateQueries({ queryKey: ['guests'] }),

  // Settings (multi-row: hr_settings, kb_settings, bs_settings, etc.)
  settings: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),

  // Traces
  traces: () => queryClient.invalidateQueries({ queryKey: ['traces'] }),
  trace_activities: () => queryClient.invalidateQueries({ queryKey: ['traces', 'activities'] }),
  trace_comments: () => queryClient.invalidateQueries({ queryKey: ['traces', 'commentCounts'] }),

  // Company contracts
  company_contracts: () => queryClient.invalidateQueries({ queryKey: ['contracts'] }),
  contract_files: () => queryClient.invalidateQueries({ queryKey: ['contracts'] }),
  rate_plan_links: () => queryClient.invalidateQueries({ queryKey: ['contracts'] }),

  // BusinessClub members (public sign-ups + manual entries)
  business_club_members: () => queryClient.invalidateQueries({ queryKey: ['businessClub'] }),

  // Sales CRM (pipeline companies + activities)
  sales_companies: () => queryClient.invalidateQueries({ queryKey: ['salesCrm'] }),
  sales_activities: () => queryClient.invalidateQueries({ queryKey: ['salesCrm', 'activities'] }),

  // Offers
  offers: () => queryClient.invalidateQueries({ queryKey: ['offers'] }),

  // Kassenbuch
  kassenbuch_entries: () => queryClient.invalidateQueries({ queryKey: ['kassenbuch', 'entries'] }),
  kassenbuch_counters: () => queryClient.invalidateQueries({ queryKey: ['kassenbuch', 'counters'] }),
  kassenbuch_locks: () => queryClient.invalidateQueries({ queryKey: ['kassenbuch', 'locks'] }),

  // Buchhaltung — settings table stays (Apaleo webhook); docs restored 2026-09
  buchhaltung_docs: () => queryClient.invalidateQueries({ queryKey: ['buchhaltung', 'docs'] }),
  buchhaltung_settings: () => queryClient.invalidateQueries({ queryKey: ['buchhaltung', 'settings'] }),

  // Inventory
  inventory_items: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'items'] }),
  inventory_history: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'history'] }),

  // Haustechnik
  ht_lists: () => queryClient.invalidateQueries({ queryKey: ['haustechnik', 'lists'] }),
  ht_devices: () => queryClient.invalidateQueries({ queryKey: ['haustechnik', 'devices'] }),
  ht_list_items: () => queryClient.invalidateQueries({ queryKey: ['haustechnik', 'listItems'] }),
  ht_config: () => queryClient.invalidateQueries({ queryKey: ['haustechnik', 'config'] }),
  ht_room_checks: () => queryClient.invalidateQueries({ queryKey: ['haustechnik', 'roomChecks'] }),

  // Sperrapartments (OOO maintenance snapshots — migration 20261016120000)
  sperrapartment_maintenances: () => queryClient.invalidateQueries({ queryKey: ['sperrapartment'] }),

  // Housekeeping
  housekeeping_rooms: () => queryClient.invalidateQueries({ queryKey: ['housekeeping', 'rooms'] }),
  housekeeping_settings: () => queryClient.invalidateQueries({ queryKey: ['housekeeping', 'settings'] }),

  // Fundsachen
  fundsachen: () => queryClient.invalidateQueries({ queryKey: ['fundsachen'] }),
  lost_and_found_activities: () => queryClient.invalidateQueries({ queryKey: ['fundsachen', 'activities'] }),

  // Waiting list
  waiting_list: () => queryClient.invalidateQueries({ queryKey: ['waitingList'] }),

  // Overbooking locks
  overbooking_locks: () => queryClient.invalidateQueries({ queryKey: ['overbookingLocks'] }),

  // Automations
  automation_configs: () => queryClient.invalidateQueries({ queryKey: ['automations', 'configs'] }),

  // Support tickets
  support_tickets: () => queryClient.invalidateQueries({ queryKey: ['supportTickets'] }),
  ticket_replies: () => queryClient.invalidateQueries({ queryKey: ['supportTickets'] }),

  // Notifications
  notifications: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),

  // Anleitungen
  anleitungen: () => queryClient.invalidateQueries({ queryKey: ['anleitungen'] }),

  // Tenants (config data)
  tenants: () => {
    queryClient.invalidateQueries({ queryKey: ['tenants'] });
    queryClient.invalidateQueries({ queryKey: ['settings', 'tenant'] });
  },

  // Loyalty
  loyalty_transactions: () => queryClient.invalidateQueries({ queryKey: ['loyalty'] }),

  // Audit log
  audit_log: () => queryClient.invalidateQueries({ queryKey: ['auditLog'] }),

  // Global logs / tasks
  global_logs: () => queryClient.invalidateQueries({ queryKey: ['globalLogs'] }),
  global_tasks: () => queryClient.invalidateQueries({ queryKey: ['globalTasks'] }),
};

/**
 * Tables to subscribe to with tenant_id filter.
 * These are the tables that have a tenant_id column and are scoped per tenant.
 */
const TENANT_SCOPED_TABLES: string[] = [
  'hr_requests',
  'users',
  'guests',
  'settings',
  'traces',
  'trace_activities',
  'trace_comments',
  'company_contracts',
  'contract_files',
  'rate_plan_links',
  'offers',
  'kassenbuch_entries',
  'kassenbuch_counters',
  'kassenbuch_locks',
  'buchhaltung_docs',
  'buchhaltung_settings',
  'inventory_items',
  'inventory_history',
  'ht_lists',
  'ht_devices',
  'ht_list_items',
  'ht_config',
  'ht_room_checks',
  'sperrapartment_maintenances',
  'housekeeping_rooms',
  'housekeeping_settings',
  'fundsachen',
  'lost_and_found_activities',
  'waiting_list',
  'overbooking_locks',
  'automation_configs',
  'support_tickets',
  'ticket_replies',
  'notifications',
  'anleitungen',
  'loyalty_transactions',
  'audit_log',
  'global_logs',
  'global_tasks',
 'shift_handovers',
 'shift_handover_items',
 'sales_companies',
 'sales_activities',
 'shifts',
 'shift_comments',
];

/**
 * Tables scoped to the individual user (filtered by user_id, not tenant_id).
 */
const USER_SCOPED_TABLES: string[] = [
  'hr_state',
  'time_logs',
  // 'shifts' moved to TENANT_SCOPED_TABLES for tenant-wide Dienstplan visibility
];

/**
 * Creates a centralized realtime store for a tenant + user.
 *
 * @param {string} tenantId - Active tenant ID.
 * @param {string} userId - Current user ID (for user-scoped tables).
 * @returns {{ start: Function, stop: Function }} Control functions.
 */
export function createRealtimeStore(tenantId: string, userId: string): { start: () => void; stop: () => void } {
  let channel: RealtimeChannel | null = null;

  function start() {
    if (channel || !tenantId) return;

    const ch = `tenant-pg-${tenantId}-${Date.now().toString(36)}`;

    // Build a single channel with all postgres_changes subscriptions
    let builder = supabase.channel(`realtime-store-${ch}`);

    // User-scoped tables (filtered by user_id)
    for (const table of USER_SCOPED_TABLES) {
      if (!userId) continue;
      const filter = `user_id=eq.${userId}`;
      builder = builder.on(
        'postgres_changes',
        { event: '*', schema: 'public', table, filter },
        (payload) => {
          const handler = TABLE_QUERY_MAP[table];
          if (handler) handler(payload);
        }
      );
    }

    // Tenant-scoped tables (filtered by tenant_id)
    for (const table of TENANT_SCOPED_TABLES) {
      const filter = `tenant_id=eq.${tenantId}`;
      builder = builder.on(
        'postgres_changes',
        { event: '*', schema: 'public', table, filter },
        (payload) => {
          const handler = TABLE_QUERY_MAP[table];
          if (handler) handler(payload);
        }
      );
    }

    channel = builder.subscribe((status, err) => {
      if (status === 'CHANNEL_ERROR') {
        console.error('[realtimeStore] Channel error:', err);
      } else if (status === 'SUBSCRIBED') {
        // Silent — don't log on every mount
      }
    });
  }

  function stop() {
    if (channel) {
      supabase.removeChannel(channel);
      channel = null;
    }
  }

  return { start, stop };
}
